import React, { Fragment, useEffect } from 'react'
import Button from '../../Button'
import Icon, { IconType } from '../../Icon'
import Col from '../../Col'
import Row from '../../Row'
import RadioSelect from '../../RadioSelect'
import FileUploader from '../../FileUploader'
import { AttachmentsFormProps } from './../types'
import styles from '@bt-react/styles'
import fileSize from '@bt-react/libs/fileSize'
import componentStyles from './AttachmentsForm.module.scss'
import classNames from 'classnames'
import { Formik, Field } from 'formik'
import { Form, FormGroup, Label, Input, FormFeedback } from 'reactstrap'
import { createSchema } from './validationSchema'
import { buttonType } from '../../Button/types'
import TooltipCentral from '../../TooltipCentral'
import sanitiseInput, { sanitiseInputAndPreventNewLine } from '@bt-react/libs/sanitiseInput'
import { useIsNarrowView } from '../../Attachments/Attachments'

const customInputForm = ({field, form: {touched, errors}, ...props}) => (
    <div>
      <Input
        invalid={!!(touched[field.name] && errors[field.name])}
        {...field}
        {...props} 
        cssModule={styles}
        maxLength={props.maxLength}
      />
        {touched[field.name] && errors[field.name] && <FormFeedback cssModule={styles}>{errors[field.name]}</FormFeedback>}
    </div>
);

export const stripUnsupportedChars = (values) => {

    const newValues = {}

    for (const [key, value] of Object.entries(values)) {

        if (key === 'title' || key === 'link') {

            newValues[key] = sanitiseInput(value || '')

        }else if (key === 'description') {

            newValues[key] = sanitiseInputAndPreventNewLine(value || '')

        } else {

            newValues[key] = value

        }

    }

    return newValues

}

const FileUploaderComponent = (props) => {
    return <FileUploader {...props} isNarrowView />
}

const AttachmentsForm: React.FC<AttachmentsFormProps> = ({userTimezone, webcastId, channelId, submitAttachmentForm, setView, status, activeResource, setting, fetchResourceSetting, isCentral}) => {

    useEffect(() => {
        if (!setting) {
            fetchResourceSetting()
        }
    }, [])

    const isNarrowView = useIsNarrowView()

    return (
        <div className={classNames({
            [componentStyles['attachments-form-container']]: true,
            [componentStyles['attachments-form-container-narrow']]: isNarrowView
        })} data-bdd="attachments-form">
            <div data-bdd="attachments-lists-instruction-text" className={componentStyles['attachments-title-text']}>
                {status.isEdit ? 'Edit' : 'Add'} a file or link to a file
            </div>
            <Formik
                validationSchema={()=>createSchema(setting)}
                initialValues={{
                    id: status.isEdit && activeResource ? activeResource.target ? activeResource.target.id : activeResource.id : '',
                    title: status.isEdit && activeResource ? activeResource.title : '',
                    description: status.isEdit && activeResource ? activeResource.description : '',
                    type:  status.isEdit ? activeResource && activeResource.file ? 'file' : 'link' : 'file',
                    file: status.isEdit ? activeResource && activeResource.file && activeResource.file.href && activeResource.file.href !== 'internal' ? {...activeResource.file, name:  activeResource.file.href.substring(activeResource.file.href.lastIndexOf('/')+1)} : null : null,
                    link:  status.isEdit && activeResource && activeResource.link ? activeResource.link.href : '',
                    channelId: channelId,
                    webcastId: webcastId
                }}
                validateOnChange={true}
                onSubmit={ async (values, {resetForm}) => {
                    const newVal = stripUnsupportedChars(values)
                    submitAttachmentForm(newVal, userTimezone)
                }}
            >
                {props => (
                    <Form onSubmit={props.handleSubmit} cssModule={styles}>
                        <FormGroup cssModule={styles} data-bdd="attachment-form-title">
                            <Label data-bdd="attachment-form-title-label" for="title" cssModule={styles} className={classNames(styles['pr-1'], styles['mt-0'], componentStyles['title-font-weight'])}>Title*</Label>
                            <TooltipCentral placement={isNarrowView ? 'top' : 'right'} data-bdd="attachment-form-title-tooltip" content="The title of the attachment.  This will be visible to audience members and will be shown in the analytics and downloadable reports" id="title-info-icon">
                                <Icon dataBdd="attachment-form-title-tooltip-info-icon" name={IconType.iconName.infoCircle} className={componentStyles['info-icon']} id="title-info-icon"/>
                            </TooltipCentral>
                            <Field data-bdd="textfield" type={'text'} name="title" id="title" placeholder="Type your title here ..." component={customInputForm} maxLength={255} />
                        </FormGroup>
                        <FormGroup cssModule={styles} data-bdd="attachment-form-description">
                            <Label data-bdd="attachment-form-description-label" for="description" cssModule={styles} className={classNames(styles['pr-1'], styles['mt-0'], componentStyles['title-font-weight'])}>Description</Label>
                            <TooltipCentral placement={isNarrowView ? 'top' : 'right'} data-bdd="attachment-form-description-tooltip" content="A short description of the attachment. Will be visible to all audience members" id="description-info-icon">
                                <Icon dataBdd="attachment-form-description-tooltip-info-icon" name={IconType.iconName.infoCircle} className={componentStyles['info-icon']} id="description-info-icon" />
                            </TooltipCentral>
                            <Field data-bdd="textfield" type={'textarea'} name="description" id="description" placeholder="Type your description here ..." component={customInputForm} />
                        </FormGroup>
                        {!status.isEdit &&
                            <FormGroup cssModule={styles} data-bdd="attachment-form-type" className={componentStyles['attachment-type-radio']}>
                                <Label data-bdd="attachment-form-type-label" for="type" cssModule={styles} className={classNames(styles['pr-1'], styles['mt-0'], componentStyles['title-font-weight'])}>Attachment type</Label>
                                <RadioSelect dataBdd="attachment-form-type-radio" value={props.initialValues.type} id="type" onChange={(id, value)=>props.setFieldValue(id, value)}>
                                    { [ { 'label': 'File', 'value': 'file' },
                                    { 'label': 'Link', 'value': 'link' } ] }
                                </RadioSelect>
                            </FormGroup>
                        }
                        <Row>
                            {props.values.type === 'file' &&
                                <Fragment>
                                    {status.isEdit &&
                                        <Col xs={9}>
                                            <Label data-bdd="attachment-form-existing-file-details-label" for="type" cssModule={styles} className={classNames(styles['pr-1'], styles['mt-0'], componentStyles['title-font-weight'])}>Existing file details</Label>
                                        </Col>
                                    }
                                    <Col xs={isNarrowView ? 12 : 8} className={classNames(styles['d-flex'], componentStyles['padding-top-bottom'])}>
                                        {!props.values.file &&
                                            <FormGroup cssModule={styles} data-bdd="attachment-file">
                                                <Field data-bdd="attachment-file-upload-field" type={'file'} name="file" id="attachment-file-select" handleChange={(value)=> props.setFieldValue('file', value)} accept={setting ? setting.supportedExtensions.replace(/;/g, ',').replace(/\*/g, '') : '*'} component={FileUploaderComponent} />
                                            </FormGroup>
                                        }
                                        {props.values.file &&
                                            <Fragment>
                                                <Icon dataBdd="attachment-file-icon" name={IconType.iconName.file} className={classNames(componentStyles['icons'], componentStyles['file-icon'])} />
                                                <span data-bdd="attachment-file-title" className={componentStyles['file-title']}>{props.values.file.name}</span>
                                                <span data-bdd="attachment-file-size" className={componentStyles['file-size']}>({props.values.file.size ? fileSize(props.values.file.size, true) : '0'})</span>
                                                {!status.isEdit &&
                                                    <span onClick={()=>props.setFieldValue('file','')} className={classNames(componentStyles['trash-icon'], componentStyles['icons'])}>
                                                        <Icon dataBdd="attachment-file-trash-icon" name={isCentral ? IconType.iconName.trashRegular : IconType.iconName.trash} className={componentStyles['trash-icon']} />
                                                    </span>
                                                }
                                                {status.isEdit &&
                                                    <FormGroup cssModule={styles} data-bdd="attachment-file">
                                                        <Field data-bdd="attachment-form-file-uploader" type={'file'} name="file" id="file" handleChange={(value)=>props.setFieldValue('file', value)} reupload={true} accept={setting ? setting.supportedExtensions.replace(/;/g, ',').replace(/\*/g, '') : '*'} component={FileUploaderComponent} />
                                                    </FormGroup>
                                                }
                                            </Fragment>
                                        }
                                    </Col>
                                </Fragment>
                            }
                            {props.values.type === 'link' &&
                                <Col xs={isNarrowView ? 12 : 8}>
                                    <FormGroup cssModule={styles} data-bdd="attachment-link-url">
                                        <Field data-bdd="attachment-link-field" type={'text'} name="link" id="link" placeholder="http:// here" component={customInputForm} />
                                    </FormGroup>
                                </Col>
                            }
                            <Col xs={isNarrowView ? 12 : 4} className={classNames(styles['pl-0'], styles['text-right'])}>
                                <Button dataBdd="attachment-cancel-button" onClick={()=>{setView('lists')}} text="Cancel" color={IconType.colour.link} className={componentStyles['cancel-btn']} type={buttonType.button} disabled={status.isSubmitting} />
                                <Button 
                                    onClick={()=>{}}
                                    color={isCentral ? IconType.colour.primary : IconType.colour.brightTALKDark}
                                    text={status.isEdit ? "Save" :"Upload"}
                                    dataBdd="attachment-submit-button"
                                    type={buttonType.submit}
                                    disabled={(!props.dirty && !status.isEdit) || !props.isValid}
                                    loading={status.isSubmitting}
                                    className={classNames(componentStyles['submit-btn'], {[componentStyles['submit-btn-central']]: isCentral})}
                                />
                            </Col>
                        </Row>
                    </Form>
                )}
            </Formik>
            <Button 
                onClick={()=>setView('lists')}
                dataBdd="attachment-back-button"
                color={IconType.colour.link}
                className={classNames(styles['pl-0'], componentStyles['back-button'])}
            >
                <Icon dataBdd="attachment-back-button-icon" name={isCentral ? IconType.iconName.angleLeftRegular : IconType.iconName.angleLeft} className={componentStyles['back-icon']} />
                Back
            </Button>
        </div>
    )

}

export default AttachmentsForm